import {
  Button,
  queryHelpers,
  useMediaQuery,
} from "@leagueplatform/genesis-core";
import { useIntl } from "@leagueplatform/locales";
import { useChatCta } from "@leagueplatform/chat";
import { useLocation, useNavigate } from "react-router-dom";
import BubbleIcon from "assets/bubble-icon.svg";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { chatInstance } from "./chat-instance";
import { UnreadMessageIndicator } from "./live-chat-unread-message-indicator.component";
import { useLiveChatAvailability } from "./use-live-chat-availability.hook";

export const LiveChatCTA = () => {
  const { formatMessage } = useIntl();
  const { isVisible, focusReturnElementRef, unreadMessageCount } =
    useChatCta<HTMLButtonElement>(chatInstance);
  // Client-side entitlement gate — hide the CTA entirely for members
  // whose LOB isn't serviced by live chat (`LIVE_CHAT_ENTITLED` from
  // `GET /v1/live-chat-configs`). EMBLEM-8963 split entitlement out of
  // the earlier `LIVE_CHAT_AVAILABLE` composite so entitled members in
  // staffed LOBs still see the bubble out of hours and land on the
  // existing CHAT_UNAVAILABLE surface via POST /v1/live-chat, rather
  // than losing the entry point entirely between 18:00 and 08:00 ET.
  // Port of Android EMBLEM-8195 updated for the EMBLEM-8962 split.
  const { isEntitled } = useLiveChatAvailability();
  const isMobile = useMediaQuery(queryHelpers.down("tablet"));
  const navigate = useNavigate();
  const location = useLocation();

  // Hide the bubble on the pre-chat and entry-point routes — those
  // screens *are* the chat surface for the moment, and the floating
  // bubble in the same corner as the composer's send button competes
  // for attention (and covers form controls on mobile). `useChatCta`
  // returns `isVisible: true` on these routes because from its POV
  // the panel isn't open — pre-chat is a route, not the panel. Guard
  // here rather than in the SDK.
  const isOnLiveChatRoute = location.pathname.startsWith(
    APP_MANAGED_ROUTES.LIVE_CHAT
  );

  if (!isVisible || isOnLiveChatRoute || !isEntitled) {
    return null;
  }

  const onClick = () => {
    const snapshot = chatInstance.actor.getSnapshot();
    // Re-open an existing session rather than starting a second one; the
    // state machine only accepts USER_START_CHAT from idle/chatEnded/error,
    // so a pre-chat run would otherwise complete against the already-open
    // session and just re-surface it after a spurious questionnaire.
    const hasActiveSession =
      snapshot.matches("inSession") || snapshot.matches("postSession");
    if (hasActiveSession) {
      chatInstance.actor.send({ type: "SET_VISIBILITY", isOpen: true });
      return;
    }
    navigate(APP_MANAGED_ROUTES.LIVE_CHAT_PRE_CHAT);
  };

  return (
    <UnreadMessageIndicator
      unreadMessageCount={unreadMessageCount}
      css={{
        position: "fixed",
        right: isMobile ? 16 : 32,
        bottom: isMobile ? 16 : 32,
        zIndex: "$sticky",
      }}
    >
      <Button
        onClick={onClick}
        ref={focusReturnElementRef}
        aria-label={
          unreadMessageCount > 0
            ? `${formatMessage({
                id: "STR_LIVE_CHAT",
                defaultMessage: "Live Chat",
              })}, ${formatMessage(
                {
                  id: "STR_LIVE_CHAT_UNREAD_MESSAGES",
                  defaultMessage:
                    "{count, plural, one {# unread message} other {# unread messages}}",
                },
                { count: unreadMessageCount }
              )}`
            : formatMessage({
                id: "STR_LIVE_CHAT",
                defaultMessage: "Live Chat",
              })
        }
        aria-haspopup="dialog"
        aria-expanded={false}
        css={{
          width: 56,
          height: 56,
          padding: 0,
          borderRadius: "50%",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img src={BubbleIcon} alt="" width={28} height={28} />
      </Button>
    </UnreadMessageIndicator>
  );
};
